import { sequencePhase, WorkflowStatus } from 'models/campaign'
import { ContactResponse } from 'models/contacts'

type SequenceMembership = Pick<ContactResponse, 'seq_membership_status'>

/**
 * Whether a contact is currently in the sequence, i.e. eligible for
 * "Remove from sequence" (truthy membership status that isn't 'removed').
 */
export function isInSequence(contact: SequenceMembership): boolean {
  return Boolean(
    contact.seq_membership_status && contact.seq_membership_status !== 'removed'
  )
}

/**
 * Whether a contact was removed from the sequence. Re-adding (un-removing) is
 * allowed in any non-completed phase — CS removes stale/internal contacts
 * during setup and needs to undo that before launch too (FIR-7990).
 */
export function wasRemovedFromSequence(contact: SequenceMembership): boolean {
  return contact.seq_membership_status === 'removed'
}

/**
 * Whether a contact was never synced into the sequence (no lead/membership —
 * the "Not in Sequence" chip). Adding these creates the EmailBison lead, which
 * only makes sense once the sequence is running: before launch they're already
 * going to be synced at launch, so there's nothing to add early (FIR-7990).
 */
export function wasNeverInSequence(contact: SequenceMembership): boolean {
  return !contact.seq_membership_status
}

/**
 * Whether the campaign phase lets this contact be added to the sequence.
 * - running: re-add a removed contact, or add a never-synced one.
 * - not_launched: only re-adding a removed contact does something (undo a
 *   setup-time removal); never-synced contacts join at launch already.
 * - completed / unknown: nothing to add.
 */
export function canAddContactToSequence(
  contact: SequenceMembership,
  campaignWorkflowStatus: WorkflowStatus | null | undefined
): boolean {
  const phase = sequencePhase(campaignWorkflowStatus)
  if (phase === 'running')
    return wasRemovedFromSequence(contact) || wasNeverInSequence(contact)
  if (phase === 'not_launched') return wasRemovedFromSequence(contact)
  return false
}

export interface SequenceBulkActionEligibility {
  /** At least one selected contact is currently in the sequence. */
  canRemoveFromSequence: boolean
  /** At least one selected contact was removed (re-addable in any live/pre-launch phase). */
  canReAddRemoved: boolean
  /** At least one selected contact was never synced (addable only while running). */
  canAddNeverSynced: boolean
}

/**
 * Compute whether the bulk "Remove from sequence" / "Add to sequence"
 * actions apply to the current selection.
 *
 * Uses the same per-contact predicates as the per-row actions in
 * ContactsTable. A bulk action is eligible when ANY selected contact
 * qualifies (the backend skips ineligible contacts). The phase gate lives in
 * getAddToSequenceDisabledReason, which combines these flags with the campaign
 * phase.
 */
export function getSequenceBulkActionEligibility(
  contacts: SequenceMembership[]
): SequenceBulkActionEligibility {
  return {
    canRemoveFromSequence: contacts.some(isInSequence),
    canReAddRemoved: contacts.some(wasRemovedFromSequence),
    canAddNeverSynced: contacts.some(wasNeverInSequence),
  }
}

export const SEQUENCE_NOT_LIVE_DISABLED_REASON =
  'Contacts are added to the sequence when the campaign launches.'
export const SEQUENCE_ENDED_DISABLED_REASON =
  "This campaign's sequence has ended."
export const NOTHING_TO_ADD_DISABLED_REASON =
  'All selected contacts are already in the sequence.'

/**
 * Disabled reason for the bulk "Add to sequence" action, or null when enabled.
 *
 * - completed: nothing to add - "sequence has ended".
 * - not_launched (incl. scheduled): only re-adding a removed contact does
 *   something (undo a setup-time removal). A selection with no removed contact
 *   is disabled with "added at launch" (never-synced contacts join at launch).
 * - running: enabled if anything is re-addable or addable.
 *
 * Mirrors the server: clear_removed runs in any non-completed phase, the
 * never-synced add only while running. Kept as a pure function because the
 * client test env is node-only, so this is the testable seam ContactsTable
 * gates on.
 */
export function getAddToSequenceDisabledReason(
  campaignWorkflowStatus: WorkflowStatus | null | undefined,
  eligibility: SequenceBulkActionEligibility
): string | null {
  const phase = sequencePhase(campaignWorkflowStatus)
  if (phase === 'completed') return SEQUENCE_ENDED_DISABLED_REASON
  if (phase === 'not_launched') {
    return eligibility.canReAddRemoved
      ? null
      : SEQUENCE_NOT_LIVE_DISABLED_REASON
  }
  if (phase === 'running') {
    return eligibility.canReAddRemoved || eligibility.canAddNeverSynced
      ? null
      : NOTHING_TO_ADD_DISABLED_REASON
  }
  // Unknown workflow status: safe default, block with the launch reason.
  return SEQUENCE_NOT_LIVE_DISABLED_REASON
}
